import type { Component } from "vue";
import type { RouteMeta } from "vue-router";

import type { ZiggyMainSiteRoutes } from "@solvari/morpheus-api";

type RouteConfig = {
  component: () => Promise<Component>;
  name: string;
  paramConstraints?: Record<string, string>;
  path: string;
};

// Ziggy routes — paths resolved from Laravel at boot, constraints applied via paramConstraints.
// Province constraint disambiguates localSearch (/{product}/{province}) from CMS catch-all (/{geoProductSlug}/{infoSlug}).
function customRoutes(provinceConstraint: string) {
  return [
    {
      name: "mainSite.homepage",
      component: () => import("~/features/homepage/pages/HomepagePage.vue"),
    },
    {
      name: "mainSite.companyWall",
      component: () => import("~/features/company-wall/pages/CompanyWall.vue"),
      paramConstraints: { page: "[1-9]\\d*" },
    },
    {
      name: "mainSite.companyWall.companyWallDetails",
      component: () =>
        import("~/features/company-wall/pages/CompanyWallDetails.vue"),
    },
    {
      name: "mainSite.localSearch.province",
      component: () =>
        import("@/features/company-wall/pages/CompanyWallProvince.vue"),
      paramConstraints: { province: provinceConstraint },
    },
    {
      name: "mainSite.localSearch.municipality",
      component: () =>
        import("@/features/company-wall/pages/CompanyWallMunicipality.vue"),
      paramConstraints: { province: provinceConstraint },
    },
    {
      name: "mainSite.localSearch.companyWallDetails",
      component: () =>
        import("@/features/company-wall/pages/CompanyWallDetails.vue"),
      paramConstraints: { province: provinceConstraint },
    },
    {
      name: "mainSite.geoProduct.leadForm",
      component: () => import("~/features/product/pages/LeadFormPage.vue"),
      meta: { focusPage: true },
    },
    {
      name: "mainSite.energy_comparison.opt_out",
      component: () =>
        import("~/features/energy-comparison/pages/EnergyComparisonOptOut.vue"),
      meta: { focusPage: true },
    },
    {
      name: "mainSite.thankYou",
      component: () =>
        import("~/features/lead-thanks/pages/LeadThanksPage.vue"),
      meta: { focusPage: true },
    },
  ] as const satisfies {
    component: () => Promise<Component>;
    meta?: RouteMeta;
    name: Parameters<ZiggyMainSiteRoutes>[0];
    paramConstraints?: Record<string, string>;
  }[];
}

// CMS routes — paths use {param} syntax, converted by ziggyPathToRouterPath.
//
// Constrained routes have paramConstraints and only match known CMS slugs.
// Each constrained blueprint gets its own route with a per-blueprint slug constraint.
// To add a new constrained blueprint: add a route here with its own constraint param,
// fetch its slugs in routing.api.ts, and pass the constraint through the builder.
//
// geoProduct rejects dotted/underscored slugs ([^/._]+) so /meta.json, /_environment
// etc. never trigger a full page render. geoProduct.info stays unconstrained because
// adding [^/._]+ to both params raises its score above staticPage's single-param
// BonusCustomRegExp, breaking nested static page priority.
// Vue Router scores constrained params higher (+10 BonusCustomRegExp), so constrained
// routes always take priority over catch-alls — order in this array is irrelevant.
//
// Route table (locale prefix omitted — /nl, /fr prepended by buildStatamicRoutes):
//
// Route                 Path                         Constraint       Matches
// ───────────────────── ──────────────────────────── ──────────────── ─────────────────────────────────────────
// productOverview       /:slug(overviewSlugs)        constrained      known overview slugs only
// staticPage            /:slug(staticPageURIs)       constrained      known static page URIs at any depth (*)
// performance           /:slug(performanceURIs)      constrained      known performance page URIs at any depth (*)
// geoProduct            /:slug([^/._]+)              catch-all        no dots/underscores — blocks /meta.json etc.
// geoProduct.info       /:geoProductSlug/:infoSlug   catch-all        any two segments not matched above

//
// Match flow for /{x}:
//   1. productOverview  — slug in overview list?          → ProductOverview
//   2. staticPage       — slug in static page URI list?   → StaticPage
//   3. performance      — slug in performance URI list?   → PerformancePage
//   4. geoProduct       — (always matches)                → ProductPage
//
// Match flow for /{x}/{y}:
//   1. staticPage       — "x/y" in static page URI list?  → StaticPage
//   2. performance      — "x/y" in performance URI list?  → PerformancePage
//   3. geoProduct.info  — (always matches)                → ProductInfoPage
//
// (*) Nested static pages: the constraint contains full URIs with slashes
//     (e.g. "about|about/team|about/team/history"). Vue Router's tokenizer
//     does not split on "/" inside a param regex group, so /:slug(about/team)
//     produces regex /^\/(about\/team)\/?$/. The constrained param scores +10
//     (BonusCustomRegExp) over unconstrained params, so it always wins priority.
//
// (**) Overlap: if a slug appears in both the overview and static page constraint
//      lists, productOverview wins because it is registered first and both have
//      equal BonusCustomRegExp score. CMS entries should not share slugs.
//
// (***) Case: Vue Router matching is case-insensitive by default. A constraint
//       "test" matches "/Test", "/TEST", etc.
//
// No dots or underscores: blocks /meta.json, /_environment, /license.txt etc.
const geoProductSlugConstraint = "[^/._]+";

type StatamicConstraints = {
  overview: string;
  performance: string;
  staticPage: string;
};

function statamicRoutes(constraints: StatamicConstraints) {
  return [
    {
      name: "mainSite.productOverview",
      path: "/{slug}",
      component: () =>
        import("~/features/product-overview/pages/ProductOverview.vue"),
      paramConstraints: { slug: constraints.overview },
    },
    {
      name: "mainSite.staticPage",
      path: "/{slug}",
      component: () => import("~/features/static/pages/StaticPage.vue"),
      paramConstraints: { slug: constraints.staticPage },
    },
    {
      name: "mainSite.performance",
      path: "/{slug}",
      component: () =>
        import("~/features/performance/pages/PerformancePage.vue"),
      paramConstraints: { slug: constraints.performance },
    },
    {
      name: "mainSite.geoProduct",
      path: "/{slug}",
      component: () => import("~/features/product/pages/ProductPage.vue"),
      paramConstraints: { slug: geoProductSlugConstraint },
    },
    {
      name: "mainSite.geoProduct.info",
      path: "/{geoProductSlug}/{infoSlug}",
      component: () => import("~/features/product/pages/ProductInfoPage.vue"),
    },
  ] as const satisfies RouteConfig[];
}

export type { StatamicConstraints };
export { customRoutes, geoProductSlugConstraint, statamicRoutes };
